import type { ComponentChildren } from "preact";
import { useCallback, useEffect, useState } from "preact/hooks";

// ------------------------------------------------------------------ routing (hash based)
export function useRoute(): string[] {
  const read = () => location.hash.replace(/^#\/?/, "").split("/").filter(Boolean);
  const [parts, setParts] = useState(read);
  useEffect(() => {
    const on = () => setParts(read());
    addEventListener("hashchange", on);
    return () => removeEventListener("hashchange", on);
  }, []);
  return parts;
}
export const go = (path: string) => (location.hash = `#/${path}`);

// ------------------------------------------------------------------ data loading
export function useLoad<T>(fn: () => Promise<T>, deps: unknown[] = []) {
  const [data, setData] = useState<T | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const reload = useCallback(() => {
    setLoading(true);
    fn()
      .then((d) => { setData(d); setError(null); })
      .catch((e: Error) => setError(e.message))
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);
  useEffect(reload, [reload]);
  return { data, error, loading, reload };
}

// ------------------------------------------------------------------ toast
let pushToast: (t: { msg: string; bad?: boolean }) => void = () => {};
export function toast(msg: string, bad = false) { pushToast({ msg, bad }); }
export function Toasts() {
  const [items, setItems] = useState<Array<{ id: number; msg: string; bad?: boolean }>>([]);
  pushToast = (t) => {
    const id = Date.now() + Math.random();
    setItems((x) => [...x, { id, ...t }]);
    setTimeout(() => setItems((x) => x.filter((i) => i.id !== id)), 5000);
  };
  return <div class="toasts" role="status">{items.map((t) => <div class={`toast ${t.bad ? "bad" : ""}`}>{t.msg}</div>)}</div>;
}

/** Wraps an async action with a busy flag and error toast. */
export function useAction() {
  const [busy, setBusy] = useState(false);
  const run = async (fn: () => Promise<unknown>, ok?: string) => {
    setBusy(true);
    try {
      await fn();
      if (ok) toast(ok);
      return true;
    } catch (e) {
      toast((e as Error).message, true);
      return false;
    } finally {
      setBusy(false);
    }
  };
  return { busy, run };
}

// ------------------------------------------------------------------ small components
export const Loading = () => <p class="muted">Loading…</p>;
export const ErrorBox = ({ error }: { error: string }) => <p class="error">{error}</p>;

export function Card({ title, actions, children }: { title?: string; actions?: ComponentChildren; children: ComponentChildren }) {
  return (
    <section class="card">
      {(title || actions) && <header><h2>{title}</h2><div class="actions">{actions}</div></header>}
      {children}
    </section>
  );
}

const STATUS_TONE: Record<string, string> = {
  met: "good", pass: "good", effective: "good", approved: "good", contracted: "good", accepted: "good", sent: "good",
  missing: "bad", expired: "bad", fail: "bad", denied: "bad", blocked: "bad", rejected: "bad", failed: "bad",
  review: "warn", unverified: "warn", info_requested: "warn", manual: "warn", unknown: "warn", pending: "warn", error: "warn",
};
export function Badge({ value }: { value: string | null | undefined }) {
  if (!value) return <span class="badge">—</span>;
  return <span class={`badge ${STATUS_TONE[value] ?? ""}`}>{value.replaceAll("_", " ")}</span>;
}

export function Modal({ title, onClose, children }: { title: string; onClose: () => void; children: ComponentChildren }) {
  useEffect(() => {
    const k = (e: KeyboardEvent) => e.key === "Escape" && onClose();
    addEventListener("keydown", k);
    return () => removeEventListener("keydown", k);
  }, [onClose]);
  return (
    <div class="modal-bg" onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div class="modal" role="dialog" aria-label={title}>
        <header><h2>{title}</h2><button class="ghost" onClick={onClose} aria-label="Close">✕</button></header>
        {children}
      </div>
    </div>
  );
}

export const fmtDate = (d?: string | null) =>
  d ? new Date(d.length === 10 ? `${d}T12:00:00` : d).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }) : "—";
export const daysUntil = (d: string) => Math.round((new Date(`${d}T12:00:00`).getTime() - Date.now()) / 86400000);

/** Reads a <form> into a plain object; empty strings become null. */
export function formData(e: Event): Record<string, string | null> {
  e.preventDefault();
  const fd = new FormData(e.currentTarget as HTMLFormElement);
  return Object.fromEntries([...fd.entries()].map(([k, v]) => [k, String(v).trim() === "" ? null : String(v).trim()]));
}
